import { AppRoutePath } from 'config/constants';
import { useAuthContext } from 'hooks/useAuth';
import { isAuthlessPathname, isAuthlessStaticPathname } from 'utils/router';

import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Wraps the route in the site's standard layout and enforces authentication and redirection.
 */
const RouteElement = ({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}): React.ReactElement => {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();

  useEffect(() => {
    document.title = title != null ? `${title} | SIRONA` : 'SIRONA';
  }, [title]);

  if (
    !isAuthenticated &&
    !isAuthlessPathname(location.pathname) &&
    !isAuthlessStaticPathname(location.pathname)
  ) {
    // Since we have a route for logout we don't want to use that one as 'previous' url.
    const state = !([AppRoutePath.LOGIN, AppRoutePath.LOGOUT] as string[]).includes(
      location.pathname
    )
      ? { from: { pathname: location.pathname, search: location.search } }
      : undefined;

    // If coming from /logout, assign href to /login to force a page refresh and reset app state
    if (AppRoutePath.LOGOUT.includes(location.pathname)) {
      window.location.href = AppRoutePath.LOGIN;
      return <></>;
    } else {
      return <Navigate to={AppRoutePath.LOGIN} state={state} />;
    }
  }

  return (
    <motion.div
      variants={pageVariants}
      initial={false}
      animate="in"
      exit="out"
      css={`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        min-width: 0;
        flex: 1 1 auto;
      `}
    >
      {children}
    </motion.div>
  );
};

const pageVariants = {
  initial: {
    opacity: 0,
    pointerEvents: 'none',
  },
  in: {
    opacity: 1,
    pointerEvents: 'auto',
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  out: {
    opacity: 0,
    pointerEvents: 'none',
    transition: { duration: 0.2, ease: 'easeIn' },
  },
} as const;

export default RouteElement;
